import { IAccount, PlanStatus } from "~/types/account";
import { ILimit, IPlan, Limits, SubscriptionType } from "~/types/billing";
import { getTotalPlanCost } from "./numbers";

export const DISABLE_PAYMENTS = false;
export const DISABLE_TRIAL_FEES = true;

export const isReturningSubscriber = (account: IAccount | null): boolean => {
  if (!account) {
    return false;
  }

  if (account.has_subscribed_before) {
    return true;
  }

  return (
    !account.active_subscription &&
    account.last_subscription?.status === PlanStatus.INACTIVE
  );
};

export const shouldShowTrialCheckoutPricing = (
  account: IAccount | null,
  isShopifyPayment: boolean
): boolean => {
  if (isShopifyPayment) {
    return false;
  }

  return !isReturningSubscriber(account);
};

/**
 * The plan whose price a plan card shows.
 *
 * A monthly winning plan is priced at its base plan plus the AI-generated
 * keywords add-on, but the add-on is only mentioned in its checkbox line.
 * Cards show the base plan's price so the fee is not folded into the plan
 * price.
 */
export const getDisplayPricePlan = (
  plan: IPlan,
  plans: IPlan[] | undefined
): IPlan => {
  if (!plan.for_winning || plan.interval !== SubscriptionType.MONTHLY) {
    return plan;
  }

  const basePlan = plans?.find(
    (p) =>
      p.status === PlanStatus.ACTIVE &&
      !p.for_winning &&
      p.interval === SubscriptionType.MONTHLY &&
      p.name === plan.name
  );

  return basePlan ?? plan;
};

export const getCheckoutAction = (
  account: IAccount | null,
  plan: IPlan | undefined
): "trial" | "downgrade" | "upgrade" | "firstSubscription" => {
  const isDowngrade =
    account?.active_subscription?.plan &&
    getTotalPlanCost(plan) < getTotalPlanCost(account.active_subscription.plan);

  if (!isReturningSubscriber(account)) {
    if (!!plan?.trial_days) {
      return "trial";
    }

    return "firstSubscription";
  }

  if (isDowngrade) {
    return "downgrade";
  }

  return "upgrade";
};

export const isFeatureEnabledOnPlan = (
  limitType: Limits,
  plan: IPlan | undefined
) => {
  if (DISABLE_PAYMENTS) return true;

  const limits: ILimit | Record<any, any> = plan?.limits || {};

  if (limitType === Limits.PAID_PLAN) {
    return !!plan;
  }

  if (!limits) return false;

  return !!limits[limitType];
};
